import { useEffect, useRef, useState } from "react";
import { IS_FREE, PREMIUM_URL, onUpsell } from "./tier";

/**
 * UpsellToast — the single toast shown when a free-tier user taps a locked
 * premium feature. Rendered once at the app root; triggered from anywhere via
 * requestUpsell() in tier.ts. Renders nothing at all in the premium build.
 *
 * Deliberately quiet: appears top-center (away from the timeline/legend pills
 * so it never fights the AttributionPill for space), auto-dismisses after a
 * few seconds, tap anywhere on it to dismiss immediately. The "See Premium"
 * link opens PREMIUM_URL in a new tab; swap that for your app-store deep link
 * / native purchase sheet when embedding in the phone app's webview.
 */
export function UpsellToast() {
  const [feature, setFeature] = useState<string | null>(null);
  // Re-arm the dismiss timer on every new request, even for the same feature.
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!IS_FREE) return;
    const off = onUpsell((label) => {
      setFeature(label);
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => setFeature(null), 4000);
    });
    return () => {
      off();
      if (timer.current) clearTimeout(timer.current);
    };
  }, []);

  if (!IS_FREE || !feature) return null;

  return (
    <div
      onClick={() => setFeature(null)}
      role="status"
      style={{
        position: "absolute",
        top: 14,
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 80, // above the menu pill (70) — a toast is transient
        display: "flex",
        alignItems: "center",
        gap: 9,
        maxWidth: "min(92vw, 420px)",
        padding: "9px 14px",
        background: "rgba(8,10,16,0.94)",
        backdropFilter: "blur(14px)",
        WebkitBackdropFilter: "blur(14px)",
        border: "1px solid rgba(245,166,35,0.35)",
        borderRadius: 999,
        boxShadow: "0 6px 20px rgba(0,0,0,0.45)",
        fontFamily: "Roboto, -apple-system, system-ui, sans-serif",
        fontSize: 12,
        lineHeight: 1.3,
        color: "#e2e5ed",
        cursor: "pointer",
        animation: "upsell-toast-in 0.18s ease-out",
      }}
    >
      <style>{`@keyframes upsell-toast-in {
        from { opacity: 0; transform: translateX(-50%) translateY(-6px); }
        to   { opacity: 1; transform: translateX(-50%) translateY(0); }
      }`}</style>
      {/* Lock glyph — amber to match the FORECAST pill's premium accent */}
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#f5a623"
        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
      </svg>
      <span style={{ minWidth: 0 }}>
        <strong style={{ fontWeight: 600 }}>{feature}</strong> is a Premium feature
      </span>
      <a
        href={PREMIUM_URL}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
        style={{
          flexShrink: 0,
          color: "#f5a623",
          fontWeight: 600,
          textDecoration: "none",
          borderLeft: "1px solid rgba(255,255,255,0.12)",
          paddingLeft: 10,
        }}
      >
        See Premium
      </a>
    </div>
  );
}
